import * as Sentry from '@sentry/react';
import React from 'react';
import { useRouteError } from 'react-router-dom';

import ErrorBoundary from './ErrorBoundary';

// ============|| ROUTE ERROR REPORTING ||============ //

/**
 * אחרי עליית גרסה, טאב שנשאר פתוח מחזיק index.html ישן וממשיך לבקש chunk-ים
 * בשמות שכבר לא קיימים בשרת — ה-import נכשל ומסך השגיאה הגלובלי מחליף את האפליקציה.
 * במקרה כזה רענון אחד פותר לחלוטין, ולכן עושים אותו אוטומטית.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|chunkloaderror/i;

/** מונע לופ רענונים אם ה-chunk באמת חסר בשרת ולא רק בקאש של הדפדפן. */
const RELOAD_GUARD_STORAGE_KEY = 'funner:chunk-reload-at';
const RELOAD_GUARD_WINDOW_MS = 60_000;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '');
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

function shouldReloadForChunkError(): boolean {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_STORAGE_KEY) ?? 0);
    if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_GUARD_STORAGE_KEY, String(Date.now()));
    return true;
  } catch {
    // sessionStorage חסום (מצב פרטי / הרשאות) — עדיף מסך שגיאה על לופ רענונים.
    return false;
  }
}

/**
 * ה-errorElement של הראוטר הציג מסך שגיאה בלי לדווח לאף מקום, כך שקריסה שלא ניתן
 * לשחזר נעלמה ללא עקבות. כאן מדווחים ל-Sentry ולקונסול עם הנתיב שבו זה קרה.
 */
export default function RouteErrorBoundary() {
  const error = useRouteError();
  const handledErrorRef = React.useRef<unknown>(null);

  React.useEffect(() => {
    // React StrictMode מריץ אפקטים פעמיים — מטפלים בכל שגיאה פעם אחת בלבד.
    if (handledErrorRef.current === error) return;
    handledErrorRef.current = error;

    const pathname = `${window.location.pathname}${window.location.search}`;
    const chunkError = isChunkLoadError(error);
    console.error(`[route-error] ${pathname}`, error);

    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { source: 'route-error-boundary', chunkLoadError: String(chunkError) },
      extra: { pathname, rawError: error },
    });

    if (chunkError && shouldReloadForChunkError()) {
      window.location.reload();
    }
  }, [error]);

  return <ErrorBoundary />;
}
